import React from 'react';
import { TFunction } from "i18next";
import Dropdown, { Option } from "react-dropdown";
import TemplateComponent, { TemplateComponentProps } from "../../containers/template_page/template_component/template_component";
import { Col, Container, Row } from "react-bootstrap";
import axios from "axios";

import ProfessorComponent, {
    orderProfessors,
    ProfessorData,
    professorKey,
} from "./professor_component";
import PageProgressBar from "../../containers/template_page/page_progress_bar/page_progress_bar";
import { API_CONFIG } from "../../config/api_config";
import "./c69_faculty.scss";
import TextSearchFilter from "../../common_components/text_search_filter/text_search_filter";
import { filterObj, toQueryString } from "../../utils/utils";


const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split('');

const MAX_PROFESSORS_RETURNED = 240;

class C69Faculty extends TemplateComponent<ProfessorData[]> {
    loadingRef = React.createRef<PageProgressBar>();

    state: {
        department: string,

        type: string,

        letter: string,

        // the current name we are searching for, locally or in the back-end
        name: string,

        // the last name searched in the back-end
        lastName: string,

        loading: boolean,

        data: ProfessorData[]
    };

    constructor(props: TemplateComponentProps<ProfessorData[]>) {
        super(props);
        
        let params = filterObj(this.props.query, ["department", "type", "letter", "name"]);
        if (!params.letter) {
            params["letter"] = '';
        }
        if (!params.name) {
            params["name"] = '';
        }
        if (params.name) {
            params.letter = '';
        }

        this.state = { ...params, ...{ lastName: params.name } };

        this.requireInternationalization = true;

        this.dataUrl = this.endpoint() + toQueryString(params);
        this.forceReload = true;
    }

    endpoint(): string {
        return `${API_CONFIG.BASE_URL}${API_CONFIG.PROFESSORS_PATH}/${this.props.language}?`;
    }

    callFilter = (remote: boolean = true) => {

        if (remote) {
            this.setState({ loading: true }, () => {
                if (this.loadingRef && this.loadingRef.current) {
                    this.loadingRef.current.reset();
                }
            });
        }

        let params: any = {};
        if (this.state.department) {
            params["department"] = this.state.department;
        }

        if (this.state.type) {
            params["type"] = this.state.type;
        }

        if (this.state.name) {
            params["name"] = this.state.name;
        }

        if (this.state.letter) {
            params["letter"] = this.state.letter;
        }

        this.updateQueryString(params);

        if (remote) {
            axios.get(this.endpoint(), { params: params }).then(res => {
                this.setState({
                    data: orderProfessors(res.data),
                    loading: false,
                    lastName: params.name ? params.name : ""
                });
            });
        }
    };

    onDataLoaded(data: ProfessorData[]) {
        this.setState({
            data: orderProfessors(data)
        });
        this.changeDepartment({value:"all",label: "C69 department all"});
        this.changeRole({value:"all",label: "C69 role all"});
    }


    changeDepartment = (selected: Option) => {
        this.setState({ department: selected.value }, () => this.callFilter());
    };

    changeRole = (selected: Option) => {
        this.setState({ type: selected.value }, () => this.callFilter());
    };

    filterByLetter = (letter: string) => {
        this.setState({ letter: (this.state.letter === letter ? '' : letter) }, () => this.callFilter());
    };

    searchUpdated = (name: string) => {

        // Search inline for incremental search
        const local = name.startsWith(this.state.lastName) && name.length >= this.state.lastName.length
            && this.state.data && this.state.data.length < MAX_PROFESSORS_RETURNED;

        this.setState({ name: name, letter: name.length > 0 ? '' : this.state.letter },
            () => this.callFilter(!local));
    };

    internationalComponentRender(data: ProfessorData[], t: TFunction): React.ReactElement {

        const DEPARTMENTS = ["all", "124", "123", "125", "126", "4421", "4422"].map(key => ({
            value: key,
            label: t("C69 department " + key)
        }));

        const ROLES = ["all", "full", "adjunct", "visiting", "emeritus"].map(key => ({
            value: key,
            label: t("C69 role " + key)
        }));

        let professors: ProfessorData[] = [];
        if (data && !!data.length) {
            professors = data;

            // with less than 300 professors we filter inline
            if (data.length < MAX_PROFESSORS_RETURNED && this.state.name) {
                let words: string[] = this.state.name.split(" ").map(word => word.toLowerCase());
                professors = professors.filter(prof =>
                    words.every(word => prof.name.toLowerCase().includes(word) || prof.surname.toLowerCase().includes(word))
                );
            }
        }

        return (
            <div className="c69-faculty auto-margins">
                {this.state.loading && <PageProgressBar ref={this.loadingRef} speed={0.1} />}
                <Container fluid={true}>
                    <Row className="filters">
                        <Col xs={12} sm={6} md={4}>
                            <Dropdown
                                key="departments-filter"
                                options={DEPARTMENTS}
                                onChange={this.changeDepartment}
                                value={this.state.department}
                                placeholder={t("C69 department placeholder")}
                                className="departments-filter"
                                menuClassName="departments-filter-menu"
                                />
                        </Col>
                        <Col xs={12} sm={6} md={4}>
                            <Dropdown
                                key="roles-filter"
                                options={ROLES}
                                onChange={this.changeRole}
                                value={this.state.type}
                                placeholder={t("C69 role placeholder")}
                                className="roles-filter" />
                        </Col>
                        <Col xs={12} md={4}>
                            <TextSearchFilter placeholder={t("C69 search placeholder")} continuous={true}
                                value={this.state.name} searchUpdated={this.searchUpdated} />
                        </Col>
                    </Row>
                    <Row>
                        {[ALPHABET.slice(0, 13), ALPHABET.slice(13, 26)].map(alphabet =>
                            <Col xs={12} sm={6} className="letter-filter" key={"letter-filter-col-" + alphabet[0]}>
                                {alphabet.map((letter) =>
                                    <span key={`letter-filter-${letter}`}
                                        className={"letter" + (letter === this.state.letter ? " active" : "")}
                                        onClick={() => this.filterByLetter(letter)}>{letter}</span>
                                )}
                            </Col>
                        )}
                    </Row>
                    <Row className="professors" >
                        {professors.length > 0 ?
                            professors.map((professor: ProfessorData) =>
                                <Col xl={4} lg={6} md={6} sm={12} xs={12} key={"professor-col-" + professorKey(professor)}>
                                    <ProfessorComponent lang={this.props.language} key={"professor-" + professorKey(professor)} {...professor} />
                                </Col>
                            )
                            :
                            <div className="cards-no-result-text">{t("C69 no result")}</div>
                        }
                    </Row>
                </Container>
            </div>
        );
    }
}

export default C69Faculty;